import { createFileRoute } from '@tanstack/react-router'
import { createPortal } from 'react-dom'
import { useState, useEffect, useRef } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'

import { HeroSection } from '@/components/home/HeroSection'
import { IntentCard } from '@/components/home/IntentCard'
import { CapabilitiesSection } from '@/components/home/CapabilitiesSection'
import { UseCasesSection } from '@/components/home/UseCasesSection'
import { QuotesSection } from '@/components/home/QuotesSection'
import { DemoCtaSection } from '@/components/home/DemoCtaSection'
import {
  CymaticBackdrop,
  SECTION_SHAPES,
  SECTION_ORDER,
  type SectionKey,
} from '@/components/home/CymaticBackdrop'

export const Route = createFileRoute('/')({
  component: LandingPage,
})

const SECTIONS: { key: SectionKey; Component: () => React.ReactElement }[] = [
  { key: 'hero', Component: HeroSection },
  { key: 'intent', Component: IntentCard },
  { key: 'capabilities', Component: CapabilitiesSection },
  { key: 'useCases', Component: UseCasesSection },
  { key: 'quotes', Component: QuotesSection },
  { key: 'demo', Component: DemoCtaSection },
]

function LandingPage() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)
  const [activeKey, setActiveKey] = useState<SectionKey>('hero')
  const [kickSignal, setKickSignal] = useState(0)
  const activeKeyRef = useRef<SectionKey>('hero')
  const sectionEls = useRef(new Map<SectionKey, HTMLElement>())
  const { scrollYProgress } = useScroll()

  // ── Ink cover over the backdrop (company-page dissolve pattern) ──
  // A fixed #181512 layer between the cymatic backdrop and the content. It
  // turns opaque while the capabilities/platform stretch is on screen (an
  // invisible flip — that stretch paints its own solid ink in-flow), then
  // DISSOLVES across the approach to use cases, revealing the living field
  // beneath the cards. The platform canvas paints the same ink, so its hard
  // edge hands off to this cover seamlessly.
  const capRef = useRef<HTMLElement | null>(null)
  const ucRef = useRef<HTMLElement | null>(null)
  const { scrollYProgress: coverIn } = useScroll({
    target: capRef,
    offset: ['start 0.5', 'start 0'],
  })
  const { scrollYProgress: coverOut } = useScroll({
    target: ucRef,
    offset: ['start end', 'start 0.4'],
  })
  const coverOpacity = useTransform(
    [coverIn, coverOut],
    ([a, b]: number[]) => Math.min(a, 1 - b),
  )

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    // eslint-disable-next-line react-hooks/set-state-in-effect -- subscribing to a media query. Seeding from `mq.matches` is the initial read of the same external system the listener below subscribes to; splitting them would leave the first paint wrong.
    setPrefersReducedMotion(mq.matches)
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // The section crossing the viewport's vertical center is "active" and selects
  // the cymatic shape. A zero-height band at the center keeps switching crisp.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const key = (entry.target as HTMLElement).dataset.section as SectionKey
            if (key) {
              const prev = activeKeyRef.current
              // Scatter the rune when leaving the hero (scroll down out of it)
              // or scrolling up out of the demo section.
              const leavingHero = prev === 'hero' && key !== 'hero'
              const upFromDemo =
                prev === 'demo' && SECTION_ORDER.indexOf(key) < SECTION_ORDER.indexOf('demo')
              if (leavingHero || upFromDemo) {
                setKickSignal((s) => s + 1)
              }
              activeKeyRef.current = key
              setActiveKey(key)
            }
          }
        }
      },
      { rootMargin: '-50% 0px -50% 0px', threshold: 0 },
    )
    sectionEls.current.forEach((el) => observer.observe(el))
    // Also observe the global footer (rendered by the root layout) so the
    // backdrop transitions into the rune as the footer scrolls into view.
    const footerEl = document.querySelector('[data-section="footer"]')
    if (footerEl) observer.observe(footerEl)
    return () => observer.disconnect()
  }, [])

  return (
    <div style={{ position: 'relative' }}>
      {SECTIONS.map(({ key, Component }) => (
        <div
          key={key}
          data-section={key}
          ref={(el) => {
            if (el) sectionEls.current.set(key, el)
            else sectionEls.current.delete(key)
            if (key === 'capabilities') capRef.current = el
            if (key === 'useCases') ucRef.current = el
          }}
        >
          <Component />
        </div>
      ))}

      {/* Living cymatic field behind the whole page (skipped for reduced motion) */}
      {!prefersReducedMotion &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            <CymaticBackdrop scrollProgress={scrollYProgress} shape={SECTION_SHAPES[activeKey]} kickSignal={kickSignal} />
            {/* Ink cover — dissolves into the use-cases section (see above) */}
            <motion.div
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 1,
                pointerEvents: 'none',
                background: '#181512',
                opacity: coverOpacity,
              }}
            />
          </>,
          document.body,
        )}
    </div>
  )
}
